import { useEffect, useState, useMemo } from 'react';
import { ShieldCheck, Eye, Clock, ArrowLeft, ArrowRight, Bookmark, MapPin, Tag, CheckCircle2, Search, ExternalLink, Phone, Send, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { blogsApi } from '@/lib/api';
import { BlogPost } from '@/lib/types';
import { linkProps } from '@/lib/router';
import { slugify } from '@/lib/format';
import { generateTopTenRealEstateBlog } from '@/lib/blogGenerator';
import { recordBusinessEnquiry } from '@/lib/geoTracker';
import fallbackBlogsData from '@/lib/fallback_blogs.json';

// Fallback high-intent guides catalog
const FALLBACK_BLOGS = [
  {
    id: 'b_solar',
    title: 'Solar Rooftop Subsidy & Net Metering Guide India (2026): PM Surya Ghar Scheme, DISCOM Approval & ROI Calculation',
    slug: 'solar-rooftop-subsidy-pm-surya-ghar-guide-india',
    category: 'Solar & Clean Energy',
    service_slug: 'solar-panel-dealers',
    service_name: 'Solar Panel Dealers & Installers',
    featured_image: '/blog-images/solar-rooftop-subsidy-pm-surya-ghar-guide-india.webp',
    excerpt: 'Complete guide to PM Surya Ghar ₹78,000 subsidy, ALMM-approved DCR modules, net metering rules, and 3kW payback calculation.',
    views: 460,
    readTime: '7 min read',
    standard: 'PM Surya Ghar & MNRE'
  },
  {
    id: 'b1',
    title: 'Why Is Your AC Leaking Water? 4 Major Causes, Fixes & Cleaning Tips (2026)',
    slug: 'why-is-your-ac-leaking-water-causes-fixes',
    category: 'Home Appliances & Repair',
    service_slug: 'ac-repair-services',
    service_name: 'AC Repair & Servicing',
    featured_image: '/blog-images/why-is-your-ac-leaking-water-causes-fixes.webp',
    excerpt: 'Step-by-step troubleshooting for clogged drain lines, dirty filters, and frozen coils. Learn why air conditioners leak water and how to fix them safely.',
    views: 380,
    readTime: '6 min read',
    standard: 'BIS IS 302-2-40'
  },
  {
    id: 'b2',
    title: 'Electric Wires for Home Wiring: How to Verify BIS IS 694 & Avoid Counterfeits',
    slug: 'electric-wires-home-wiring-bis-is-694-guide',
    category: 'Electrical & Hardware',
    service_slug: 'electricians',
    service_name: 'Electricians & Wiring Contractors',
    featured_image: '/blog-images/electric-wires-home-wiring-bis-is-694-guide.webp',
    excerpt: 'Crucial guide for house wiring in India. Learn how to verify mandatory BIS IS 694:2010 certification and spot fake wire brands using the official BIS CARE app.',
    views: 420,
    readTime: '7 min read',
    standard: 'IS 694:2010 CM/L'
  },
  {
    id: 'b3',
    title: 'AC Gas Filling Guide (2026): Types of Refrigerants (R32 vs R410A), Signs & Cost',
    slug: 'ac-gas-filling-guide-cost-refrigerant-types',
    category: 'Home Appliances & Repair',
    service_slug: 'ac-repair-services',
    service_name: 'AC Gas Refilling & Compressor Repair',
    featured_image: '/blog-images/ac-gas-filling-guide-cost-refrigerant-types.webp',
    excerpt: 'When does your AC actually need gas refilling? Compare R32 vs R410A refrigerants, standard charging costs, and how to verify nitrogen pressure leak repairs.',
    views: 310,
    readTime: '5 min read',
    standard: 'BEE ISEER & MoEFCC'
  },
  {
    id: 'b4',
    title: 'Pest Control Services in India: CIB&RC Chemical Safety & Treatment Guide',
    slug: 'pest-control-services-india-chemical-safety-guide',
    category: 'Home Maintenance',
    service_slug: 'pest-control-services',
    service_name: 'Pest Control & Termite Treatment',
    featured_image: '/blog-images/pest-control-services-india-chemical-safety-guide.webp',
    excerpt: 'How to select a safe, licensed pest control service. Verify CIB&RC approved chemical formulations, herbal gels, and long-term termite protection warranties.',
    views: 345,
    readTime: '6 min read',
    standard: 'CIB&RC Registered'
  },
  {
    id: 'b5',
    title: 'AC Bill Calculation Guide India: How Much Electricity Does Your AC Really Consume?',
    slug: 'ac-electricity-bill-calculation-guide-india',
    category: 'Home Appliances & Repair',
    service_slug: 'ac-repair-services',
    service_name: 'AC Dealers & Power Servicing',
    featured_image: '/blog-images/ac-electricity-bill-calculation-guide-india.webp',
    excerpt: 'Calculate your exact monthly AC power consumption and power bill in India with formulas, BEE 3-star vs 5-star efficiency comparisons, and power saving tips.',
    views: 290,
    readTime: '8 min read',
    standard: 'BEE Star Labeling'
  }
];

const TOP_METRO_CITIES = [
  { name: 'Chennai', slug: 'chennai', state: 'Tamil Nadu' },
  { name: 'Delhi NCR', slug: 'delhi-ncr', state: 'Delhi' },
  { name: 'Bengaluru', slug: 'bengaluru', state: 'Karnataka' },
  { name: 'Hyderabad', slug: 'hyderabad', state: 'Telangana' },
  { name: 'Mumbai', slug: 'mumbai', state: 'Maharashtra' },
  { name: 'Pune', slug: 'pune', state: 'Maharashtra' },
  { name: 'Kolkata', slug: 'kolkata', state: 'West Bengal' },
  { name: 'Ahmedabad', slug: 'ahmedabad', state: 'Gujarat' },
  { name: 'Coimbatore', slug: 'coimbatore', state: 'Tamil Nadu' },
  { name: 'Kochi', slug: 'kochi', state: 'Kerala' },
  { name: 'Jaipur', slug: 'jaipur', state: 'Rajasthan' },
  { name: 'Chandigarh', slug: 'chandigarh', state: 'Punjab / Haryana' },
  { name: 'Thane', slug: 'thane', state: 'Maharashtra' },
  { name: 'Noida', slug: 'noida', state: 'Uttar Pradesh' },
  { name: 'Gurugram', slug: 'gurugram', state: 'Haryana' },
  { name: 'Lucknow', slug: 'lucknow', state: 'Uttar Pradesh' }
];

// Top Verified Real Estate Projects for Blog Innerlinks & Sidebar
const FEATURED_REAL_ESTATE_PROJECTS = [
  {
    name: 'Casagrand Madelyn',
    builder: 'Casagrand',
    slug: 'casagrand-madelyn-ground-level-comprised-in-surv-chennai',
    location: '237/3B, Chennai',
    price: '₹58 L - ₹1.10 Cr',
    image: '/project-images/casagrand-madelyn/hero.webp',
    rera: 'TN/1/BUILDING/0605/2024'
  },
  {
    name: 'Urbanrise Revolution One',
    builder: 'Alliance Group',
    slug: 'urbanrise-revolution-one-padur-old-mahabalipuram-road-omr-chennai',
    location: 'Padur, OMR',
    price: '₹45 L - ₹95 L',
    image: '/project-images/urbanrise-revolution-one/hero.webp',
    rera: 'TN/01/BUILDING/0014/2019'
  },
  {
    name: 'Casagrand Spacio',
    builder: 'Casagrand',
    slug: 'casagrand-spacio-comprised-in-survey-numbers-chennai',
    location: 'Perumbakkam, Chennai',
    price: '₹62 L - ₹1.25 Cr',
    image: '/project-images/casagrand-spacio/hero.webp',
    rera: 'TNRERA/32/BLG/0055/2026'
  },
  {
    name: 'Casagrand Ascentia',
    builder: 'Casagrand',
    slug: 'casagrand-ascentia-chennai-metropolitan-area-chennai',
    location: 'Thoraipakkam, OMR',
    price: '₹75 L - ₹1.65 Cr',
    image: '/project-images/casagrand-ascentia/hero.webp',
    rera: 'TNRERA/11/BLG/0070/2026'
  }
];

export default function BlogDetailPage({ slug }: { slug: string }) {
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);

  // Sidebar Form State
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadNote, setLeadNote] = useState('');
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  // 1. Dynamic Canonical Tag Injection
  useEffect(() => {
    let link: HTMLLinkElement | null = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `https://zhasearch.com/blog/${slug}`);
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    let isMounted = true;

    void (async () => {
      // Fetch current blog post from FastAPI PostgreSQL backend
      try {
        const data = await blogsApi.getBlogBySlug(slug);
        if (data && isMounted) {
          setBlog(data as BlogPost);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn('FastAPI blog fetch fallback:', e);
      }

      if (isMounted) {
        const fullFb = (fallbackBlogsData as any[]).find(b => b.slug === slug);
        if (fullFb && fullFb.content) {
          setBlog(fullFb as BlogPost);
        } else {
          const fb = FALLBACK_BLOGS.find(b => b.slug === slug);
          if (fb) {
            setBlog({
              id: fb.id,
              title: fb.title,
              slug: fb.slug,
              meta_title: fb.title,
              meta_description: fb.excerpt,
              focus_keyword: fb.category,
              author: 'Zha Search Editorial Desk',
              featured_image: fb.featured_image,
              content: fullFb?.content || '',
              views: fb.views,
              status: 'published',
              city: 'Chennai',
              category: fb.category,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          } else {
            const cityMatch = slug.match(/in-([a-z-]+)$/);
            const cityName = cityMatch ? cityMatch[1].replace(/-/g, ' ').toUpperCase() : 'THANE';
            const categoryMatch = slug.replace(/^top-10-best-/, '').replace(/-in-[a-z-]+$/, '').replace(/-/g, ' ');
            const categoryName = categoryMatch ? categoryMatch.toUpperCase() : 'WINE SHOPS';
            const generated = generateTopTenRealEstateBlog(cityName, categoryName, []);
            setBlog({
              id: 'preview',
              title: generated.title,
              slug: generated.slug,
              meta_title: generated.meta_title,
              meta_description: generated.meta_description,
              focus_keyword: generated.focus_keyword,
              author: generated.author,
              featured_image: generated.featured_image,
              content: generated.content,
              views: 128,
              status: 'published',
              city: cityName,
              category: categoryName,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          }
        }
      }

      if (isMounted) setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Dynamically update document title, meta description, and SEO tags for extensions and crawlers
  useEffect(() => {
    if (!blog) return;
    const pageTitle = blog.meta_title ? (blog.meta_title.includes('Zha') ? blog.meta_title : `${blog.meta_title} | Zha Search`) : `${blog.title} | Zha Search`;
    document.title = pageTitle;

    const rawContent = blog.content ? blog.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '';
    const metaDesc = blog.meta_description || (rawContent ? rawContent.slice(0, 158) + '...' : `${blog.title} - Complete guide and analysis on Zha Search.`);

    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    descTag.setAttribute('content', metaDesc);

    // OpenGraph Tags
    const setMetaProp = (prop: string, val: string) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', prop);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', val);
    };

    setMetaProp('og:title', pageTitle);
    setMetaProp('og:description', metaDesc);
    setMetaProp('og:url', window.location.href);
    setMetaProp('og:type', 'article');
    if (blog.featured_image) setMetaProp('og:image', blog.featured_image);

    // Twitter Cards
    const setMetaName = (name: string, val: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', val);
    };
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', pageTitle);
    setMetaName('twitter:description', metaDesc);
    if (blog.featured_image) setMetaName('twitter:image', blog.featured_image);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://zhasearch.com/blog/${blog.slug}`);
  }, [blog]);

  // Determine current topic info & service keywords
  const topicMeta = useMemo(() => {
    const fb = FALLBACK_BLOGS.find(b => b.slug === slug);
    if (fb) return fb;

    if (slug.includes('solar') || slug.includes('surya') || slug.includes('rooftop')) {
      return {
        service_slug: 'solar-panel-dealers',
        service_name: 'Solar Panel Dealers & Installers',
        category: 'Solar & Clean Energy',
        standard: 'PM Surya Ghar & MNRE'
      };
    }
    if (slug.includes('wire') || slug.includes('electric')) {
      return {
        service_slug: 'electricians',
        service_name: 'Electricians & Electrical Contractors',
        category: 'Electrical & Hardware',
        standard: 'IS 694:2010 CM/L'
      };
    }
    if (slug.includes('pest') || slug.includes('termite')) {
      return {
        service_slug: 'pest-control-services',
        service_name: 'Pest Control & Termite Treatment',
        category: 'Home Maintenance',
        standard: 'CIB&RC Safety Standard'
      };
    }
    if (slug.includes('bill') || slug.includes('power')) {
      return {
        service_slug: 'ac-repair-services',
        service_name: 'AC Dealers & Power Servicing',
        category: 'Home Appliances & Repair',
        standard: 'BEE Star Labeling Scheme'
      };
    }
    if (
      slug.includes('real-estate') ||
      slug.includes('parandur') ||
      slug.includes('villa') ||
      slug.includes('flat') ||
      slug.includes('plot') ||
      slug.includes('rera') ||
      slug.includes('cmda') ||
      slug.includes('wetland') ||
      slug.includes('carpet-area') ||
      slug.includes('resale') ||
      slug.includes('metro') ||
      slug.includes('omr')
    ) {
      return {
        service_slug: 'builders',
        service_name: 'Builders & Property Developers',
        category: 'Real Estate',
        standard: 'TNRERA & CMDA Compliance'
      };
    }
    return {
      service_slug: 'ac-repair-services',
      service_name: 'AC Repair & Servicing',
      category: 'Home Appliances & Repair',
      standard: 'BIS IS 302-2-40'
    };
  }, [slug]);

  // Top 5 blogs for the right-side widget
  const topBlogs = useMemo(() => {
    return FALLBACK_BLOGS.slice(0, 5);
  }, []);

  // Related blogs for bottom section (excluding current)
  const relatedBlogs = useMemo(() => {
    return FALLBACK_BLOGS.filter(b => b.slug !== slug).slice(0, 4);
  }, [slug]);

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadPhone.trim()) return;
    setLeadSubmitting(true);
    try {
      void recordBusinessEnquiry({
        listing_id: 'blog_lead',
        listing_name: topicMeta.service_name,
        name: leadName || 'Website Visitor',
        phone: leadPhone,
        message: `Blog: ${blog?.title || ''} | Requirement: ${leadNote || 'Direct Quote'}`,
        category: topicMeta.category,
        city: blog?.city || 'Chennai',
        ip: '127.0.0.1'
      });
      await supabase.from('leads').insert([
        {
          name: leadName || 'Website Visitor',
          phone: leadPhone,
          service: topicMeta.service_name,
          city: blog?.city || 'Chennai',
          note: `Source: Blog (${blog?.title}) | Note: ${leadNote || 'Direct Quote Request'}`,
          status: 'new',
          created_at: new Date().toISOString()
        }
      ]);
      setLeadSuccess(true);
      setLeadName('');
      setLeadPhone('');
      setLeadNote('');
    } catch (err) {
      console.warn('Lead submission recorded locally:', err);
      setLeadSuccess(true);
    } finally {
      setLeadSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center space-y-3">
        <div className="animate-spin text-purple-700 text-3xl mx-auto">⏳</div>
        <p className="text-sm font-bold text-slate-600">Loading Guide & Verification Standards...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">Guide Not Found</h1>
        <p className="text-xs text-slate-500">The requested buyer or regulatory guide could not be found.</p>
        <a {...linkProps('/blogs')} className="inline-block bg-purple-700 text-white text-xs font-extrabold px-6 py-3 rounded-xl shadow-md">
          Explore All Guides
        </a>
      </div>
    );
  }

  return (
    <div className="bg-slate-50/50 min-h-screen pb-20">
      {/* 🧭 Breadcrumbs Navigation */}
      <div className="bg-white border-b border-slate-200/80 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs font-semibold text-slate-500 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <a {...linkProps('/')} className="hover:text-purple-700 transition shrink-0">Home</a>
          <span>/</span>
          <a {...linkProps('/blogs')} className="hover:text-purple-700 transition shrink-0">Buyer Guides</a>
          <span>/</span>
          <span className="text-slate-900 font-bold truncate">{blog.title}</span>
        </div>
      </div>

      {/* 🌟 2-Column Responsive Layout (Article on Left + Sidebar on Right) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-start">
          
          {/* 📄 LEFT COLUMN: MAIN ARTICLE (col-span-8) */}
          <main className="lg:col-span-8 space-y-8">
            <article className="bg-white text-slate-900 shadow-sm rounded-3xl border border-slate-200/80 p-6 sm:p-8 space-y-8">
              {/* Meta Header */}
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-xs font-extrabold text-purple-700 uppercase tracking-wider">
                    <span className="bg-purple-50 border border-purple-200 px-2.5 py-0.5 rounded-md">
                      🏷️ {topicMeta.category}
                    </span>
                    <span>•</span>
                    <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-0.5 rounded-md">
                      🛡️ {topicMeta.standard}
                    </span>
                  </div>

                  <button
                    onClick={() => setBookmarked(v => !v)}
                    className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-purple-700 bg-slate-100 px-3 py-1 rounded-full transition cursor-pointer"
                  >
                    <Bookmark className={`h-3.5 w-3.5 ${bookmarked ? 'fill-purple-700 text-purple-700' : ''}`} />
                    <span>{bookmarked ? 'Saved' : 'Bookmark'}</span>
                  </button>
                </div>

                <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 leading-tight">
                  {blog.title}
                </h1>

                <div className="flex items-center justify-between border-y border-slate-200 py-3.5 text-xs text-slate-700 font-semibold flex-wrap gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-purple-700 text-white font-black flex items-center justify-center text-xs shadow-xs">
                      Z
                    </div>
                    <div>
                      <div className="font-extrabold text-slate-900">{blog.author || 'Zha Search Editorial Desk'}</div>
                      <div className="text-[10px] text-slate-500">E-E-A-T Verified Standard</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-slate-600 font-bold">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5 text-purple-600" />
                      {blog.views || 380} Views
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      6 min read
                    </span>
                    <span>📅 {blog.created_at ? new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
              </div>

              {/* Featured Cover Image (100% Fully Visible with reserved aspect-ratio & High Priority) */}
              {blog.featured_image && (
                <div className="w-full aspect-[16/9] rounded-3xl overflow-hidden shadow-md border border-slate-200 bg-slate-950 flex items-center justify-center">
                  <img
                    src={blog.featured_image}
                    alt={blog.title}
                    width="1200"
                    height="675"
                    loading="eager"
                    fetchPriority="high"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              {/* Rich Blog Body Content (Sanitized to prevent duplicate hero images) */}
              <div
                className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-black prose-h2:text-xl prose-h2:text-slate-900 prose-h2:mt-8 prose-p:text-slate-800 prose-p:leading-relaxed prose-a:text-purple-700 prose-a:font-bold hover:prose-a:underline text-sm font-medium text-slate-900"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    let c = blog.content || '';
                    if (blog.featured_image) {
                      const featName = blog.featured_image.split('/').pop() || '';
                      if (featName) {
                        c = c.replace(new RegExp(`^\\s*(?:<!--\\s*HERO SECTION\\s*-->\\s*)?<div class="relative overflow-hidden rounded-3xl[^"]*">\\s*<img[^>]+${featName.replace('.', '\\.')}[^>]*>[\\s\\S]*?<\\/div>\\s*<\\/div>\\s*`, 'i'), '');
                      }
                    }
                    c = c.replace(/<!--\s*Featured Hero Banner Image.*?-->\s*<div[^>]*>.*?<img[^>]*>.*?<\/div>/si, '');
                    // Only convert SECTION badges (inside <span> badges) to STEP, never legal statutes like 'Section 3 of RERA'
                    c = c.replace(/(<span[^>]*>)\s*SECTION\s+(\d+)\s*(<\/span>)/gi, '$1STEP $2$3');
                    return c;
                  })()
                }}
              />

              {/* Author Bio Box */}
              <div className="bg-purple-50/70 border border-purple-200 rounded-3xl p-6 flex items-start gap-4 text-purple-950">
                <div className="w-12 h-12 rounded-2xl bg-purple-700 text-white font-black text-lg flex items-center justify-center shrink-0 shadow-sm">
                  Z
                </div>
                <div className="space-y-1">
                  <div className="font-extrabold text-sm text-purple-950">
                    Verified by {blog.author || 'Zha Search Editorial Desk'}
                  </div>
                  <p className="text-xs text-purple-900 leading-relaxed font-medium">
                    Technical standards and consumer verification guide published by Zha Search. All regulatory citations (BIS, BEE, CIB&RC, MNRE, RERA) are cross-referenced with statutory gazette notifications.
                  </p>
                </div>
              </div>
            </article>

            {/* 🏙️ LOCAL DIRECTORY SERVICES IN TOP CITIES */}
            <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-extrabold text-purple-700 uppercase tracking-wider bg-purple-100 px-2 py-0.5 rounded">
                    Verified Local Directory
                  </span>
                  <h3 className="text-lg sm:text-xl font-black font-display text-slate-900 mt-1">
                    Find {topicMeta.service_name} Across Top Indian Cities
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    Connect with verified, GST-compliant local professionals and request instant free estimates.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pt-2">
                {TOP_METRO_CITIES.map(city => {
                  const searchRoute = `/search/${topicMeta.service_slug}-in-${city.slug}`;
                  return (
                    <a
                      key={city.slug}
                      {...linkProps(searchRoute)}
                      className="p-3 bg-slate-50 hover:bg-purple-50 border border-slate-200 hover:border-purple-300 rounded-2xl text-left transition group shadow-2xs hover:shadow-xs flex flex-col justify-between"
                      title={`Verified ${topicMeta.service_name} in ${city.name}`}
                    >
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-black text-slate-900 group-hover:text-purple-700 transition">
                          <MapPin className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                          <span>{city.name}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 font-medium pl-5 mt-0.5 line-clamp-1">
                          {topicMeta.service_name.split('&')[0]} in {city.name}
                        </div>
                      </div>
                      <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex items-center justify-between text-[10px] font-bold text-purple-700 group-hover:underline">
                        <span>View Verified</span>
                        <span>→</span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </section>
          </main>

          {/* 📌 RIGHT COLUMN: ORDERED SIDEBAR WITH STICKY LEAD FORM AT BOTTOM */}
          <aside className="lg:col-span-4 mt-8 lg:mt-0 space-y-6">
            
            {/* 📰 WIDGET 1: TOP 5 BUYER & REGULATORY GUIDES */}
            <div className="bg-white rounded-3xl p-5 border border-slate-200/90 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-1.5 text-xs font-black text-slate-900">
                  <ShieldCheck className="h-4 w-4 text-purple-700" />
                  <span>Top Regulatory Guides</span>
                </div>
                <a {...linkProps('/blogs')} className="text-[10px] font-extrabold text-purple-700 hover:underline">
                  View All
                </a>
              </div>

              <div className="space-y-3.5">
                {topBlogs.map(tBlog => {
                  const isCurrent = tBlog.slug === slug;
                  return (
                    <a
                      key={tBlog.id}
                      {...linkProps(`/blog/${tBlog.slug}`)}
                      className={`flex items-center gap-3 p-2 rounded-2xl transition group ${
                        isCurrent ? 'bg-purple-50 border border-purple-200' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="w-16 h-12 aspect-[4/3] rounded-xl bg-slate-900 overflow-hidden shrink-0 relative border border-slate-200">
                        <img
                          src={tBlog.featured_image}
                          alt={tBlog.title}
                          width="160"
                          height="120"
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition"
                        />
                      </div>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="text-[9px] font-extrabold text-purple-700 uppercase">
                          {tBlog.category.split('&')[0]}
                        </div>
                        <h4 className="text-xs font-bold text-slate-900 group-hover:text-purple-700 transition leading-snug line-clamp-2">
                          {tBlog.title}
                        </h4>
                        <div className="text-[9px] text-slate-600 font-semibold flex items-center gap-2">
                          <span>👁️ {tBlog.views}</span>
                          <span>•</span>
                          <span>{tBlog.readTime}</span>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* 🏢 WIDGET 2: FEATURED RERA PROJECTS IN CHENNAI (FOR REAL ESTATE BLOGS) */}
            {topicMeta.category === 'Real Estate' ? (
              <div className="bg-white rounded-3xl p-5 border border-slate-200/90 shadow-sm space-y-3.5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-1.5 text-xs font-black text-slate-900">
                    <span className="text-emerald-600 text-sm">🏢</span>
                    <span>Top Verified Projects</span>
                  </div>
                  <a {...linkProps('/projects')} className="text-[10px] font-extrabold text-emerald-700 hover:underline">
                    View 12,200+
                  </a>
                </div>

                <div className="space-y-2.5">
                  {FEATURED_REAL_ESTATE_PROJECTS.map((proj) => (
                    <a
                      key={proj.slug}
                      {...linkProps(`/project/${proj.slug}`)}
                      className="p-2.5 rounded-2xl border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/40 transition flex items-center gap-3 group"
                    >
                      <div className="w-14 h-12 rounded-xl bg-slate-900 overflow-hidden shrink-0 border border-slate-200">
                        <img
                          src={proj.image}
                          alt={proj.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=300&q=80';
                          }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-extrabold text-xs text-slate-900 group-hover:text-emerald-700 transition truncate">
                          {proj.name}
                        </div>
                        <div className="text-[10px] text-slate-500 font-medium truncate">
                          by {proj.builder} • {proj.location}
                        </div>
                        <div className="text-[10px] font-black text-emerald-700 mt-0.5">
                          {proj.price}
                        </div>
                      </div>
                      <span className="text-slate-400 group-hover:text-emerald-600 text-xs font-bold shrink-0">→</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              /* 📍 Original Popular Cities Directory for Service Blogs */
              <div className="bg-white rounded-3xl p-5 border border-slate-200/90 shadow-sm space-y-3">
                <div className="text-xs font-black text-slate-900 border-b border-slate-100 pb-2 flex items-center justify-between">
                  <span>{topicMeta.service_name.split('&')[0]} in Top Metros</span>
                  <span className="text-[10px] font-bold text-slate-400">1-Click Search</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {TOP_METRO_CITIES.slice(0, 8).map(city => (
                    <a
                      key={city.slug}
                      {...linkProps(`/search/${topicMeta.service_slug}-in-${city.slug}`)}
                      className="px-2.5 py-1.5 bg-slate-50 hover:bg-purple-50 hover:text-purple-700 border border-slate-200 rounded-xl text-[11px] font-bold text-slate-700 transition flex items-center justify-between"
                    >
                      <span>{city.name}</span>
                      <span className="text-slate-400 text-[10px]">→</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* 🏷️ WIDGET 3: EXPLORE GUIDE CATEGORIES */}
            <div className="bg-white rounded-3xl p-5 border border-slate-200/90 shadow-sm space-y-3">
              <div className="text-xs font-black text-slate-900">
                Guide Categories
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  'Real Estate',
                  'Solar & Clean Energy',
                  'Home Appliances',
                  'Electrical & Wiring',
                  'Pest Control',
                  'Power & Utilities'
                ].map(c => (
                  <a
                    key={c}
                    {...linkProps(`/blogs?category=${slugify(c)}`)}
                    className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-purple-50 hover:text-purple-700 text-[11px] font-bold text-slate-700 border border-slate-200 transition"
                  >
                    {c}
                  </a>
                ))}
              </div>
            </div>

            {/* ⚡ WIDGET 4: STICKY ENQUIRY / LEAD FORM (Kept below Guide Categories & Sticky on scroll) */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white rounded-3xl p-6 shadow-xl border border-slate-700 space-y-4 sticky top-24 z-20">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-purple-300 uppercase tracking-wider">
                  <Sparkles className="h-3 w-3 text-amber-300 animate-pulse" />
                  <span>{topicMeta.category === 'Real Estate' ? 'Free Project Site Visit' : 'Free Instant Quotes'}</span>
                </div>
                <h3 className="text-base font-extrabold text-white">
                  {topicMeta.category === 'Real Estate' ? 'Looking to Buy in Chennai?' : `Need ${topicMeta.service_name.split('&')[0]}?`}
                </h3>
                <p className="text-xs text-slate-300">
                  {topicMeta.category === 'Real Estate'
                    ? 'Connect directly with verified developer sales desks and TNRERA legal advisors.'
                    : 'Connect with top-rated, certified local service providers in your city.'}
                </p>
              </div>

              {leadSuccess ? (
                <div className="p-4 bg-emerald-500/20 border border-emerald-400/40 rounded-2xl text-center space-y-1 text-emerald-200">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400 mx-auto" />
                  <div className="text-xs font-bold text-white">Estimate Request Sent!</div>
                  <p className="text-[10px] text-emerald-300">Verified technicians will contact you shortly.</p>
                  <button
                    onClick={() => setLeadSuccess(false)}
                    className="text-[10px] text-purple-300 underline pt-1 cursor-pointer"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} className="space-y-3">
                  <div>
                    <input
                      type="text"
                      value={leadName}
                      onChange={e => setLeadName(e.target.value)}
                      placeholder="Your Name (Optional)"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white/10 border border-white/20 text-xs text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      value={leadPhone}
                      onChange={e => setLeadPhone(e.target.value)}
                      required
                      placeholder="Mobile Number (e.g. 9876543210)*"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white/10 border border-white/20 text-xs text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={leadNote}
                      onChange={e => setLeadNote(e.target.value)}
                      placeholder={topicMeta.category === 'Real Estate' ? 'Project Name / Preferred Budget' : 'Specific Requirement / Issue'}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white/10 border border-white/20 text-xs text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={leadSubmitting}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 active:scale-98 text-white font-extrabold text-xs rounded-xl shadow-lg shadow-purple-900/40 transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <Send className="h-3.5 w-3.5" />
                    <span>{leadSubmitting ? 'Sending...' : topicMeta.category === 'Real Estate' ? 'Book Site Walkthrough' : 'Request Free Callback'}</span>
                  </button>

                  <div className="pt-1 flex items-center justify-center gap-3 text-[10px] text-slate-400">
                    <span>✓ 100% Free</span>
                    <span>•</span>
                    <span>✓ Verified Pros</span>
                    <span>•</span>
                    <span>✓ No Spam</span>
                  </div>
                </form>
              )}
            </div>

          </aside>
        </div>
      </div>

      {/* 📚 FULL-WIDTH BOTTOM SECTION: RELATED STATUTORY & BUYER GUIDES (Matching 2nd Reference Image) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/90 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div>
              <span className="text-[10px] font-extrabold text-purple-700 uppercase tracking-wider bg-purple-100 px-2 py-0.5 rounded">
                Recommended Reading
              </span>
              <h3 className="text-xl sm:text-2xl font-black font-display text-slate-900 mt-1">
                Related Statutory & Buyer Guides
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Browse verified checklists, statutory Indian safety standards, and equipment repair benchmarks.
              </p>
            </div>
            <a
              {...linkProps('/blogs')}
              className="text-xs font-extrabold text-purple-700 hover:underline flex items-center gap-1 shrink-0"
            >
              <span>View All Guides</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedBlogs.map(rBlog => (
              <a
                key={rBlog.id}
                {...linkProps(`/blog/${rBlog.slug}`)}
                className="bg-slate-50 hover:bg-white border border-slate-200 hover:border-purple-300 rounded-2xl overflow-hidden transition group shadow-2xs hover:shadow-md flex flex-col justify-between"
              >
                <div>
                  <div className="aspect-[16/9] w-full bg-slate-900 overflow-hidden relative">
                    <img
                      src={rBlog.featured_image}
                      alt={rBlog.title}
                      width="400"
                      height="225"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-1.5 left-1.5 bg-slate-950/80 backdrop-blur-xs text-white text-[8px] font-mono font-bold px-1.5 py-0.5 rounded">
                      {rBlog.standard}
                    </div>
                  </div>

                  <div className="p-4 space-y-2">
                    <div className="text-[9px] font-black text-purple-700 uppercase tracking-wider">
                      {rBlog.category}
                    </div>
                    <h4 className="text-xs font-black text-slate-900 group-hover:text-purple-700 transition leading-snug line-clamp-2">
                      {rBlog.title}
                    </h4>
                  </div>
                </div>

                <div className="p-4 pt-0 text-[11px] font-bold text-slate-500 flex items-center justify-between border-t border-slate-200/50 mt-2">
                  <span>👁️ {rBlog.views} views</span>
                  <span className="text-purple-700 group-hover:underline flex items-center gap-0.5">
                    <span>Read</span>
                    <span>→</span>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>
      </div>

      {/* 🚀 Sticky Bottom Action Bar on Mobile */}
      <div className="lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 backdrop-blur-md border border-white/20 text-white px-5 py-2.5 rounded-full shadow-2xl flex items-center gap-4 text-xs font-bold w-[90%] max-w-sm justify-between">
        <a {...linkProps('/blogs')} className="hover:text-purple-300 transition flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>All Guides</span>
        </a>
        <a
          {...linkProps(`/search/${topicMeta.service_slug}-in-chennai`)}
          className="bg-purple-600 hover:bg-purple-700 px-3.5 py-1.5 rounded-full text-white font-extrabold shadow-sm transition"
        >
          Find {topicMeta.service_name.split('&')[0]} →
        </a>
      </div>
    </div>
  );
}
